/**
 * VerifyRollupBanner — header pill + caption that summarizes the Verify tab.
 *
 * Bill 2026-05-21 Person Modal Spec-Lock §7.4.2 + canonical-walkthrough SPEC
 * Addendum 5.2 (Verify-tab compensating-doc clearance state machine). Reads
 * the `person.extra.verifyClearance` map, runs the rollup through
 * `verifyClearance.rollupVerifyState`, and renders:
 *
 *   - green  for `pass` (with comp-doc badge when applicable)
 *   - blue   for `pending` (info)
 *   - amber  for `fsr-override` (caption: "Overridden by [officerCode]")
 *   - red    for `fail`
 *
 * Stateless presentation; consumers update `person.extra.verifyClearance`
 * via `upsertSection` and re-render. Section sub-panels (OfacScanSection,
 * IndividualAmlInsightSection, AddressSection, etc.) emit their own audit
 * SessionEvents via `useEmitEvent` — this banner is summary-only.
 */
import { Banner, Pill } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import {
  captionForState,
  labelForSection,
  rollupVerifyState,
  toClearanceVector,
  toneForState,
  type VerifyClearanceMap,
  type VerifyRollup,
  type VerifySection,
} from './verifyClearance';

export interface VerifyRollupBannerProps {
  person: RaoPerson;
  /**
   * When `true`, render the compact pill-only variant (no Banner wrapper).
   * Used by the PersonDialog header status strip. Defaults to `false`
   * (full banner with caption + per-section chips).
   */
  compact?: boolean;
}

function readVerifyMap(person: RaoPerson): VerifyClearanceMap | undefined {
  const extra = (person as unknown as { extra?: Record<string, unknown> }).extra;
  const raw = extra?.['verifyClearance'];
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as VerifyClearanceMap;
}

function pillVariantForRollup(rollup: VerifyRollup): 'ok' | 'warn' | 'err' | 'info' {
  switch (rollup.state) {
    case 'pass':
    case 'clear-via-comp-doc':
      return 'ok';
    case 'fsr-override':
      return 'warn';
    case 'fail':
      return 'err';
    case 'pending':
      return 'info';
  }
}

function pillLabel(rollup: VerifyRollup): string {
  switch (rollup.state) {
    case 'pass': return 'Verify: Cleared';
    case 'clear-via-comp-doc': return 'Verify: Cleared (comp doc)';
    case 'pending': return 'Verify: Pending';
    case 'fsr-override': return 'Verify: Overridden';
    case 'fail': return 'Verify: Review required';
  }
}

const SECTION_ORDER: ReadonlyArray<VerifySection> =
  ['ln', 'ofac', 'idScan', 'address', 'phone', 'email'];

export function VerifyRollupBanner({
  person,
  compact = false,
}: VerifyRollupBannerProps): JSX.Element {
  const map = readVerifyMap(person);
  const rollup = rollupVerifyState(toClearanceVector(map));

  if (compact) {
    return (
      <Pill variant={pillVariantForRollup(rollup)}>{pillLabel(rollup)}</Pill>
    );
  }

  const tone = toneForState(rollup.state);
  const caption = captionForState(rollup);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
      aria-label="Verify tab rollup"
    >
      <Banner tone={tone} title={pillLabel(rollup)}>
        {caption}
      </Banner>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          fontSize: 12,
          color: 'var(--ink-700)',
        }}
      >
        {SECTION_ORDER.map((section) => {
          const row = map?.[section];
          if (!row) {
            return (
              <Pill key={section} variant="muted">
                {section === 'email' ? 'Email: not checked' : `${labelForSection(section)}: unrun`}
              </Pill>
            );
          }
          const variant =
            row.state === 'pass' || row.state === 'clear-via-comp-doc' ? 'ok'
            : row.state === 'fsr-override' ? 'warn'
            : row.state === 'fail' ? 'err'
            : 'info';
          const label =
            row.state === 'fsr-override' && row.fsrOfficerCode
              ? `${labelForSection(section)}: override (${row.fsrOfficerCode})`
              : section === 'idScan' && row.reason === 'on-file-expiry'
                // Only the expiry date of the ID on file was checked - nothing was scanned.
                ? `ID on file: ${row.state === 'pass' ? 'not expired' : row.state === 'fail' ? 'expired' : 'expires soon'}`
                // CHG-0051: a blank e-mail in branch is allowed, not checked - say so instead of "pass".
                : section === 'email' && row.reason === 'not-required'
                  ? 'Email: not required'
                  : `${labelForSection(section)}: ${row.state}`;
          return (
            <Pill key={section} variant={variant}>{label}</Pill>
          );
        })}
      </div>
    </div>
  );
}
